import { useEffect, useState } from "react";
import { Users, Search, PlusCircle, Scale, Download, CheckCircle2, Landmark, Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatR } from "@/lib/api";
import { BackButton } from "@/components/BackButton";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function AdminStokvel() {
  const [members, setMembers] = useState(null);
  const [q, setQ] = useState("");
  const [banking, setBanking] = useState("");
  const [savingBanking, setSavingBanking] = useState(false);
  const [termsVersion, setTermsVersion] = useState("");
  const [detail, setDetail] = useState(null);
  const [entry, setEntry] = useState({ type: "contribution", amount: "", status: "verified", note: "" });
  const [posting, setPosting] = useState(false);

  const loadMembers = (query = q) =>
    api.get("/admin/stokvel/members", { params: query ? { q: query } : {} })
      .then((r) => { setMembers(r.data.members); setBanking(r.data.banking_details || ""); setTermsVersion(r.data.terms_version); })
      .catch((e) => toast.error(errMsg(e)));

  const loadDetail = (uid) =>
    api.get(`/admin/stokvel/members/${uid}`).then((r) => setDetail(r.data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { loadMembers(""); }, []);

  const saveBanking = async () => {
    setSavingBanking(true);
    try {
      await api.put("/admin/stokvel/settings", { banking_details: banking });
      toast.success("Banking details saved — now visible to activated members");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingBanking(false); }
  };

  const postEntry = async (e) => {
    e.preventDefault();
    if (!entry.note.trim()) { toast.error("A note/reason is required"); return; }
    setPosting(true);
    try {
      await api.post(`/admin/stokvel/members/${detail.wallet.user_id}/entries`, {
        type: entry.type, amount: parseFloat(entry.amount), status: entry.status, note: entry.note.trim(),
      });
      toast.success(`${entry.type === "contribution" ? "Contribution" : "Adjustment"} recorded`);
      setEntry({ type: "contribution", amount: "", status: "verified", note: "" });
      await loadDetail(detail.wallet.user_id);
      loadMembers("");
    } catch (err) { toast.error(errMsg(err)); } finally { setPosting(false); }
  };

  const verifyEntry = async (id) => {
    try {
      await api.post(`/admin/stokvel/entries/${id}/verify`);
      toast.success("Contribution marked as verified");
      await loadDetail(detail.wallet.user_id);
      loadMembers("");
    } catch (err) { toast.error(errMsg(err)); }
  };

  const downloadStatement = () => {
    api.get(`/admin/stokvel/members/${detail.wallet.user_id}/statement.csv`, { responseType: "blob" })
      .then((r) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(r.data);
        a.download = `stokvel-statement-${detail.wallet.reference}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="space-y-6" data-testid="admin-stokvel">
      <div>
        <h2 className="font-display text-2xl font-bold text-navy">Stationery Stokvel</h2>
        <p className="text-sm text-slate-500 mt-0.5">Members, EFT contributions (verify them once the money reflects), adjustments and statements.</p>
      </div>

      {!detail && (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-5" data-testid="stokvel-banking-editor">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><Landmark size={13} /> EFT banking details shown to members</p>
          <textarea value={banking} onChange={(e) => setBanking(e.target.value)} rows={3}
            placeholder={"e.g. Bank: FNB · Account name: BTMEDU (PTY) LTD · Account no: … · Branch code: … · Reference: your STK- number"}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="stokvel-banking-input" />
          <button onClick={saveBanking} disabled={savingBanking} className="mt-2 h-9 px-5 rounded-full bg-navy text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="stokvel-banking-save">
            <Save size={13} /> {savingBanking ? "Saving…" : "Save banking details"}
          </button>
        </div>
      )}

      {detail ? (
        <div className="space-y-5" data-testid="stokvel-member-detail">
          <div>
            <BackButton onClick={() => { setDetail(null); loadMembers(""); }} label="Back to members" testId="stokvel-detail-back" />
          </div>

          <div className="bg-white rounded-3xl border border-slate-200/80 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display text-xl font-bold text-navy" data-testid="stokvel-member-name">{detail.member?.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{detail.member?.email}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Reference <span className="font-mono font-bold text-navy">{detail.wallet.reference}</span> · Terms v{detail.wallet.terms_version} accepted {fmtDate(detail.wallet.accepted_at)} · Member since {fmtDate(detail.wallet.created_at)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Available balance</p>
                <p className="font-display text-3xl font-extrabold text-navy" data-testid="stokvel-member-balance">{formatR(detail.balance)}</p>
                {detail.pending > 0 && <p className="text-[11px] text-amber-600 font-semibold">{formatR(detail.pending)} pending verification</p>}
              </div>
            </div>
            <button onClick={downloadStatement} className="mt-4 h-9 px-4 rounded-full border border-navy/30 text-navy text-xs font-bold flex items-center gap-1.5 hover:bg-navy hover:text-white transition-colors" data-testid="stokvel-statement-download">
              <Download size={13} /> Download statement (CSV)
            </button>
          </div>

          <form onSubmit={postEntry} className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="stokvel-entry-form">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Record a contribution or adjustment</p>
            <div className="flex gap-2 flex-wrap items-center">
              <select value={entry.type} onChange={(e) => setEntry({ ...entry, type: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white" data-testid="stokvel-entry-type">
                <option value="contribution">EFT contribution (money in)</option>
                <option value="adjustment">Adjustment (+/- correction, reason required)</option>
              </select>
              <input type="number" step="0.01" min="0.01" required value={entry.amount} onChange={(e) => setEntry({ ...entry, amount: e.target.value })}
                placeholder={entry.type === "adjustment" ? "Amount (+/-)" : "Amount (R)"}
                className="w-36 h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="stokvel-entry-amount" />
              {entry.type === "contribution" && (
                <select value={entry.status} onChange={(e) => setEntry({ ...entry, status: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm bg-white" data-testid="stokvel-entry-status">
                  <option value="verified">Verified — money reflected in the bank</option>
                  <option value="pending">Pending — proof received, not yet confirmed</option>
                </select>
              )}
              <input value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} required
                placeholder={entry.type === "contribution" ? "e.g. EFT received 12 Nov, FNB statement" : "Reason for this adjustment"}
                className="flex-1 min-w-[220px] h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="stokvel-entry-note" />
              <button disabled={posting} className="h-10 px-5 rounded-full bg-gold hover:bg-gold-hover text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50" data-testid="stokvel-entry-submit">
                {entry.type === "contribution" ? <PlusCircle size={15} /> : <Scale size={15} />} {posting ? "Saving…" : "Record"}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Every entry is stored permanently with your admin email and timestamp — history is never edited or deleted. The first verified contribution automatically carries the once-off R50 admin fee (never charged twice).</p>
          </form>

          <div className="bg-white rounded-3xl border border-slate-200/80 p-6">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Ledger ({detail.ledger.length} entries)</p>
            {detail.ledger.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6" data-testid="stokvel-ledger-empty">No entries yet.</p>
            ) : (
              <div className="space-y-1.5" data-testid="stokvel-ledger">
                {detail.ledger.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0 text-sm" data-testid={`stokvel-admin-entry-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-navy truncate">{e.note}</p>
                      <p className="text-[11px] text-slate-400">
                        {fmtDate(e.created_at)} · {e.type}{e.order_number ? ` · ${e.order_number}` : ""} · by {e.created_by}
                      </p>
                    </div>
                    {e.status === "pending" ? (
                      <button onClick={() => verifyEntry(e.id)} className="h-7 px-3 rounded-full bg-amber-50 text-amber-700 text-[11px] font-bold flex items-center gap-1 hover:bg-amber-100 shrink-0" data-testid={`stokvel-verify-${e.id}`}>
                        <CheckCircle2 size={11} /> Verify
                      </button>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 shrink-0">Verified</span>
                    )}
                    <span className={`font-mono font-bold shrink-0 ${e.amount >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                      {e.amount >= 0 ? "+" : ""}{formatR(e.amount)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {detail.orders.length > 0 && (
            <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="stokvel-orders">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Orders paid from Stokvel balance</p>
              {detail.orders.map((o) => (
                <div key={o.order_number} className="flex justify-between text-sm py-1.5 border-b border-slate-50 last:border-0">
                  <span className="font-mono font-bold text-navy">{o.order_number}</span>
                  <span className="text-slate-500">{fmtDate(o.created_at)}</span>
                  <span className="font-mono font-bold">{formatR(o.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6" data-testid="stokvel-members-card">
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => { setQ(e.target.value); loadMembers(e.target.value); }}
                placeholder="Search name, email or reference…"
                className="w-full h-10 rounded-xl border border-slate-200 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-gold" data-testid="stokvel-search" />
            </div>
            <span className="text-[11px] text-slate-400">Terms version: v{termsVersion}</span>
          </div>
          {!members ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading members…</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="stokvel-no-members">No Stokvel members yet — they activate the wallet from their customer profile.</p>
          ) : (
            <div className="space-y-2" data-testid="stokvel-members">
              {members.map((m) => (
                <button key={m.user_id} onClick={() => loadDetail(m.user_id)}
                  className="w-full flex items-center gap-4 rounded-2xl border border-slate-100 hover:border-gold p-4 text-left transition-colors"
                  data-testid={`stokvel-member-${m.reference}`}>
                  <span className="w-9 h-9 rounded-xl bg-gold/10 text-gold flex items-center justify-center shrink-0"><Users size={15} /></span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-navy text-sm truncate">{m.name} <span className="font-mono text-xs text-slate-400 ml-1">{m.reference}</span></p>
                    <p className="text-[11px] text-slate-400">{m.email} · terms v{m.terms_version} · since {fmtDate(m.created_at)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-mono font-bold text-navy">{formatR(m.balance)}</p>
                    {m.pending > 0 && <p className="text-[10px] text-amber-600 font-semibold">{formatR(m.pending)} pending</p>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
